import { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  HStack,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { Avatar } from '../Avatar';
import { CheckCheck } from 'lucide-react';
import { useAtomValue } from 'jotai';
import type { ChatParticipant } from './ConversationDialogs';
import { replyLabel, type ChatMessage } from './messageUtils';
import { memberLabelsAtom } from '../../store';
import { api } from '../../lib/api';
import { formatClock, formatDate } from '../../lib/format';
import { useT } from '../../i18n';

interface MessageInfoSheetProps {
  /** The live message (its `readBy` follows `message:read`). */
  message: ChatMessage | null;
  participants: ChatParticipant[];
  currentUserId: string | undefined;
  /** The chat's family: faces open through it (D292). */
  familyId: string | null | undefined;
  isGroup: boolean;
  onClose: () => void;
}

/**
 * Message info (M62, docs/chat-round-four.md D227): when my message went,
 * who has seen it and who not yet. Read watermarks (D122) don't say when
 * someone read it, so a row says "Seen" and no time. There is no
 * "Delivered": the server doesn't track it.
 */
export function MessageInfoSheet({
  message,
  participants,
  currentUserId,
  familyId,
  isGroup,
  onClose,
}: MessageInfoSheetProps) {
  const t = useT();
  const memberLabels = useAtomValue(memberLabelsAtom);
  const phone = useBreakpointValue({ base: true, md: false }) ?? true;
  // Fresh from the server when the sheet opens, so a `message:read` missed
  // while the socket was down doesn't leave it stale; offline, `readBy`.
  const [fetched, setFetched] = useState<{
    messageId: string;
    seen: string[];
  } | null>(null);
  const messageId = message?.id ?? null;
  useEffect(() => {
    if (!messageId) return;
    let cancelled = false;
    api
      .getMessageReceipts(messageId)
      .then((response) => {
        if (cancelled || !response.success) return;
        setFetched({
          messageId,
          seen: response.data.seen.map((entry) => entry.userId),
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [messageId]);

  if (!message) return null;

  const seenIds = new Set([
    ...message.readBy,
    ...(fetched?.messageId === message.id ? fetched.seen : []),
  ]);
  const others = participants.filter((p) => p.id !== currentUserId);
  const seen = others.filter((p) => seenIds.has(p.id));
  const notYet = others.filter((p) => !seenIds.has(p.id));
  const when = (iso: string) => {
    const date = new Date(iso);
    return t('chat.info.when', {
      date: formatDate(date),
      time: formatClock(date),
    });
  };
  const preview =
    message.type === 'text' ? message.content : replyLabel(message);

  const row = (participant: ChatParticipant, read: boolean) => (
    <HStack
      key={participant.id}
      spacing={3}
      minH="48px"
      data-testid={read ? 'info-seen' : 'info-not-yet'}
    >
      <Avatar
        size="sm"
        name={participant.displayName}
        src={participant.avatarUrl ?? undefined}
        ownerId={participant.id}
        familyId={familyId}
      />
      <Text flex="1" fontSize="16px" noOfLines={1}>
        {memberLabels[participant.id] ?? participant.displayName}
      </Text>
      {read ? (
        <HStack spacing={1} color="accent" flexShrink={0}>
          <CheckCheck size={16} aria-hidden="true" />
          <Text fontSize="14px" fontWeight={600}>
            {t('chat.info.seen')}
          </Text>
        </HStack>
      ) : (
        <Text fontSize="14px" color="muted" flexShrink={0}>
          {t('chat.info.notYet')}
        </Text>
      )}
    </HStack>
  );

  const sectionLabel = (text: string) => (
    <Text
      fontSize="13px"
      fontWeight={700}
      color="muted"
      textTransform="uppercase"
      letterSpacing="0.04em"
      pt={2}
    >
      {text}
    </Text>
  );

  const content = (
    <VStack align="stretch" spacing={3}>
      <Heading as="h2" fontSize="22px">
        {t('chat.info.title')}
      </Heading>
      <Box
        bg="accent.tint"
        borderRadius="16px"
        px={4}
        py={3}
        borderWidth="1px"
        borderColor="line"
      >
        <Text fontSize="15px" noOfLines={3} whiteSpace="pre-wrap">
          {preview}
        </Text>
      </Box>
      <Box>
        <Text fontSize="14px" color="muted">
          {t('chat.info.sent', { when: when(message.createdAt) })}
        </Text>
        {message.editedAt && (
          <Text fontSize="14px" color="muted">
            {t('chat.info.edited', { when: when(message.editedAt) })}
          </Text>
        )}
      </Box>
      {isGroup ? (
        <>
          {sectionLabel(`${t('chat.info.seenBy')} · ${seen.length}`)}
          {seen.length === 0 ? (
            <Text fontSize="15px" color="muted">
              {t('chat.info.nobody')}
            </Text>
          ) : (
            seen.map((participant) => row(participant, true))
          )}
          {notYet.length > 0 && (
            <>
              {sectionLabel(`${t('chat.info.notYet')} · ${notYet.length}`)}
              {notYet.map((participant) => row(participant, false))}
            </>
          )}
        </>
      ) : (
        // A direct chat: one line (D227).
        others.map((participant) =>
          row(participant, seenIds.has(participant.id))
        )
      )}
    </VStack>
  );

  if (phone) {
    return (
      <Drawer isOpen placement="bottom" onClose={onClose}>
        <DrawerOverlay bg="rgba(31, 29, 27, 0.5)" />
        <DrawerContent
          borderTopRadius="28px"
          bg="surface"
          maxH="92dvh"
          pb="max(env(safe-area-inset-bottom), 16px)"
        >
          <DrawerBody px={5} pt="10px" pb={2} overflowY="auto">
            <Box
              w="40px"
              h="5px"
              borderRadius="full"
              bg="line"
              mx="auto"
              mb={4}
            />
            {content}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Modal isOpen onClose={onClose} isCentered size="md">
      <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
      <ModalContent mx={5} borderRadius="28px" bg="surface">
        <ModalCloseButton />
        <ModalBody px={5} py={6}>
          {content}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
